import moment from 'moment';
import { Reducer } from 'redux';
import { ECustomerTypeView } from '../user/types';
import {
  ELongSteelOrdersStatus,
  EOrderTab,
  IBilledOrderData,
  IBilledOrderTracking,
  ICgCertificateResponse,
  IDownloadNFEResponse,
  ILongSteelOrderData,
  ILongSteelOrdersResponse,
  ILongSteelOrdersState,
  IOpenOrderData,
  IQCFileResponse,
  ITrackingFlatSteel,
  ITrackingLongSteel,
  Types
} from './types';

const startDate = moment().subtract(1, 'months').format('YYYY-MM-DD');
const endDate = moment().format('YYYY-MM-DD');

const LONG_STEEL_ORDERS_INITIAL_STATE: ILongSteelOrdersState = {
  loading: false,
  error: false,
  items: [],
  divisions: [],
  initialInputValues: {
    states: [],
    emitterIds: [],
    receiverId: '',
    startDate: startDate,
    endDate: endDate,
    orderStatus: ELongSteelOrdersStatus.ALL,
    tab: EOrderTab.OPEN
  },

  lastLoadTime: undefined,
  initialized: false
};

type TCustomAction = {
  type: Types;
  payload: {
    ordersResponse: ILongSteelOrdersResponse;

    bpError: boolean;

    openOrderItem: IOpenOrderData;
    openOrderItemId: string;
    trackingFlatSteel: ITrackingFlatSteel;
    trackingLongSteel: ITrackingLongSteel;

    billedOrderItem: IBilledOrderData;
    billedOrderTracking: IBilledOrderTracking;

    tab: EOrderTab;
    customerViewType?: ECustomerTypeView;

    qcFileResponse: IQCFileResponse;
    downloadNFEResponse: IDownloadNFEResponse;

    states: string[];
    customerIds: string[];
    startDate?: string;
    endDate?: string;

    cgCqResponse: ICgCertificateResponse;
  };
};

const reducer: Reducer<ILongSteelOrdersState, TCustomAction> = (
  state = LONG_STEEL_ORDERS_INITIAL_STATE,
  action
) => {
  switch (action.type) {
    case Types.LOAD_LONG_STEEL_ORDERS:
    case Types.LOAD_LONG_STEEL_ORDERS_FROM_LOCAL_STORAGE:
      return {
        ...state,
        initialInputValues: {
          ...state.initialInputValues,
          states: action.payload.states || [],
          emitterIds: action.payload.customerIds || [],
          startDate: action.payload.startDate,
          endDate: action.payload.endDate
        },
        items: [],
        receivers: [],
        divisions: [],
        loading: true,
        error: false,
        initialized: true,
        lastLoadTime: undefined
      };

    case Types.LOAD_LONG_STEEL_ORDERS_SUCCESS:
      return {
        ...state,
        ...action.payload.ordersResponse,
        loading: false,
        error: false
      };

    case Types.LOAD_LONG_STEEL_ORDERS_FAILURE:
      return {
        ...state,
        items: [],
        loading: false,
        error: true,
        bpError: action.payload.bpError
      };

    // OPEN ORDER TRACKING FLAT STEEL
    case Types.LOAD_TRACKING_FLAT_STEEL:
      return {
        ...state,
        items: setTrackingItemFlatSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItem,
          true,
          false
        )
      };

    case Types.LOAD_TRACKING_FLAT_STEEL_SUCCESS:
      return {
        ...state,
        items: setTrackingItemFlatSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItem,
          false,
          false,
          action.payload.trackingFlatSteel
        )
      };

    case Types.LOAD_TRACKING_FLAT_STEEL_FAILURE:
      return {
        ...state,
        items: setTrackingItemFlatSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItem,
          false,
          true
        )
      };

    // OPEN ORDER TRACKING LONG STEEL
    case Types.LOAD_TRACKING_LONG_STEEL:
      return {
        ...state,
        items: setTrackingItemLongSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItemId,
          true,
          false
        )
      };

    case Types.LOAD_TRACKING_LONG_STEEL_SUCCESS:
      return {
        ...state,
        items: setTrackingItemLongSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItemId,
          false,
          false,
          action.payload.trackingLongSteel
        )
      };

    case Types.LOAD_TRACKING_LONG_STEEL_FAILURE:
      return {
        ...state,
        items: setTrackingItemLongSteelStateInOpenOrder(
          state.items,
          action.payload.openOrderItemId,
          false,
          true
        )
      };

    // BILLED ORDER TRACKING
    case Types.LOAD_BILLED_ORDER_TRACKING:
      return {
        ...state,
        items: setTrackingItemSteelStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          true,
          false
        )
      };

    case Types.LOAD_BILLED_ORDER_TRACKING_SUCCESS:
      return {
        ...state,
        items: setTrackingItemSteelStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.billedOrderTracking
        )
      };

    case Types.LOAD_BILLED_ORDER_TRACKING_FAILURE:
      return {
        ...state,
        items: setTrackingItemSteelStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          true
        )
      };

    // CERTIFICADO DE QUALIDADE
    case Types.LOAD_QC_FILE:
    case Types.CG_QC_FILE_REQUEST:
      return {
        ...state,
        items: setQCStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          true,
          false
        )
      };

    case Types.LOAD_QC_FILE_SUCCESS:
      return {
        ...state,
        items: setQCStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.qcFileResponse
        )
      };

    case Types.LOAD_QC_FILE_FAILURE:
      return {
        ...state,
        items: setQCStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          true
        )
      };

    case Types.CG_QC_FILE_REQUEST_SUCCESS:
      return {
        ...state,
        items: setStatusOnBilledOrderItem(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.cgCqResponse
        )
      };

    case Types.CG_QC_FILE_REQUEST_FAILURE:
      return {
        ...state,
        items: setStatusOnBilledOrderItem(
          state.items,
          action.payload.billedOrderItem,
          false,
          true
        )
      };

    case Types.CG_QC_FILE_DOWNLOAD:
      return {
        ...state,
        items: setStatusOnBilledOrderItem(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          undefined,
          true
        )
      };

    case Types.DOWNLOAD_QC_FILE:
      return {
        ...state,
        items: setQCStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.billedOrderItem.qcState
            .qcFileResponse as IQCFileResponse,
          true
        )
      };

    // NF
    case Types.LOAD_NF_FILE:
      return {
        ...state,
        items: setNFStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          true,
          false
        )
      };

    case Types.LOAD_NF_FILE_SUCCESS:
      return {
        ...state,
        items: setNFStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.downloadNFEResponse
        )
      };

    case Types.LOAD_NF_FILE_FAILURE:
      return {
        ...state,
        items: setNFStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          true
        )
      };

    case Types.DOWNLOAD_NF_FILE:
      return {
        ...state,
        items: setNFStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.billedOrderItem.nfState.downloadNFEResponse,
          true
        )
      };

    // NOTA DE REMESSA
    case Types.LOAD_SHIPPING_NOTE_FILE:
      return {
        ...state,
        items: setShippingNoteStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          true,
          false
        )
      };

    case Types.LOAD_SHIPPING_NOTE_FILE_SUCCESS:
      return {
        ...state,
        items: setShippingNoteStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.downloadNFEResponse
        )
      };

    case Types.LOAD_SHIPPING_NOTE_FILE_FAILURE:
      return {
        ...state,
        items: setShippingNoteStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          true
        )
      };

    case Types.DOWNLOAD_SHIPPING_NOTE_FILE:
      return {
        ...state,
        items: setShippingNoteStateInBilledOrder(
          state.items,
          action.payload.billedOrderItem,
          false,
          false,
          action.payload.billedOrderItem.shippingNoteState
            ? action.payload.billedOrderItem.shippingNoteState
                .downloadNFEResponse
            : undefined,
          true
        )
      };

    case Types.CLEAR_ORDERS_STATE:
      return {
        ...LONG_STEEL_ORDERS_INITIAL_STATE,
        initialized: true,
        initialInputValues: {
          ...LONG_STEEL_ORDERS_INITIAL_STATE.initialInputValues,
          states:
            action.payload.customerViewType === undefined ||
            action.payload.customerViewType === ECustomerTypeView.RECEIVER ||
            action.payload.customerViewType ===
              ECustomerTypeView.FABRICATION_RECEIVER
              ? []
              : state.initialInputValues.states,
          emitterIds:
            action.payload.customerViewType === undefined ||
            action.payload.customerViewType === ECustomerTypeView.RECEIVER ||
            action.payload.customerViewType ===
              ECustomerTypeView.FABRICATION_RECEIVER
              ? []
              : state.initialInputValues.emitterIds,
          receiverId:
            action.payload.customerViewType === undefined ||
            action.payload.customerViewType === ECustomerTypeView.EMITTER
              ? ''
              : state.initialInputValues.receiverId,
          tab: action.payload.tab
        }
      };

    default:
      return state;
  }
};

const getItemFromLongSteelData = (
  items: ILongSteelOrderData[],
  selectedItemId: string
) => {
  return items.findIndex((item) => item.id === selectedItemId);
};

const setTrackingItemFlatSteelStateInOpenOrder = (
  items: ILongSteelOrderData[],
  selectedItem: IOpenOrderData,
  loading: boolean,
  error: boolean,
  trackingFlatSteel?: ITrackingFlatSteel
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IOpenOrderData).trackingFlatSteel = {
      loading,
      error,
      flatsteel: trackingFlatSteel
    };
  }

  return items;
};

const setTrackingItemLongSteelStateInOpenOrder = (
  items: ILongSteelOrderData[],
  openOrderItemId: string,
  loading: boolean,
  error: boolean,
  trackingLongSteel?: ITrackingLongSteel
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, openOrderItemId);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IOpenOrderData).trackingLongSteel = {
      loading,
      error,
      longSteel: trackingLongSteel
    };
  }

  return items;
};

const setTrackingItemSteelStateInBilledOrder = (
  items: ILongSteelOrderData[],
  selectedItem: IBilledOrderData,
  loading: boolean,
  error: boolean,
  billedOrderTracking?: IBilledOrderTracking
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IBilledOrderData).trackingState = {
      loading,
      error,
      tracking: billedOrderTracking
    };
  }

  return items;
};

const setQCStateInBilledOrder = (
  items: ILongSteelOrderData[],
  selectedItem: IBilledOrderData,
  loading: boolean,
  error: boolean,
  qcFileResponse?: IQCFileResponse,
  alreadyDownloaded?: boolean
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    const foundItem = items[selectedItemIdx] as IBilledOrderData;
    foundItem.qcState = {
      ...foundItem.qcState,
      loading,
      error,
      qcFileResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setNFStateInBilledOrder = (
  items: ILongSteelOrderData[],
  selectedItem: IBilledOrderData,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IBilledOrderData).nfState = {
      ...(items[selectedItemIdx] as IBilledOrderData).nfState,
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setShippingNoteStateInBilledOrder = (
  items: ILongSteelOrderData[],
  selectedItem: IBilledOrderData,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): ILongSteelOrderData[] => {
  const selectedItemIdx = getItemFromLongSteelData(items, selectedItem.id);
  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IBilledOrderData).shippingNoteState = {
      ...(items[selectedItemIdx] as IBilledOrderData).shippingNoteState,
      loading,
      error,
      downloadNFEResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setStatusOnBilledOrderItem = (
  items: ILongSteelOrderData[],
  // futuramente será um array?
  selectedItem: IBilledOrderData,
  loading: boolean,
  error: boolean,
  cgQcResponse?: ICgCertificateResponse,
  alreadyDownloaded = false
): ILongSteelOrderData[] => {
  const idx = getItemFromLongSteelData(items, selectedItem.id);
  if (idx !== -1) {
    const item = items[idx] as IBilledOrderData;
    if (cgQcResponse) {
      // Também mudará quando download em batch existir
      const response = cgQcResponse.content[0];
      item.billingHistory = {
        ...item.billingHistory,
        statusQC: response.status
      };
      item.qcState = {
        ...item.qcState,
        cgQcCertificateResponse: response
      };
    }
    item.qcState = {
      ...item.qcState,
      loading,
      error,
      alreadyDownloaded
    };
  }
  return items;
};

export default reducer;
